import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { selectUser } from '../profile/selectors';
import type { ChatProgressHistoryEntry } from '../types/signlaRTypes';
import { AIResponseStatusType } from '../types/aiChatbot';

const getAIChatbotConversationsState = (state: RootState) => state.aiChatbot;
const emptyProgressHistory: ChatProgressHistoryEntry[] = [];

export const selectAIChatSenderFirstName = createSelector(
  [selectUser],
  user => user?.firstName?.trim() || undefined
);

const selectActiveAIResponse = createSelector(
  [getAIChatbotConversationsState],
  (state) => {
    return (Object.values(state.activeResponseByLegacyTarget) as string[])
      .map(responseId => state.htmlSnapshotResponses[responseId])
      .find(item => item && !item.isComplete && item.agentId === state.activeChatbotVariant);
  }
);

export const selectAIChatProgress = createSelector(
  [selectActiveAIResponse],
  response => response?.progressSuppressed ? 'hidden' : response?.progress?.stage
);

export const selectStoppableAIResponseId = createSelector(
  [selectActiveAIResponse],
  response => response && (!response.streamingStopped || response.backendCancellationStatus === 'failed')
    ? response.responseId : undefined
);

export const selectIsAIResponseStopped = createSelector(
  [selectActiveAIResponse],
  response => response?.streamingStopped === true
);

export const selectAIChatProgressHistory = createSelector(
  [selectActiveAIResponse],
  response => response?.progressHistory ?? emptyProgressHistory
);

export const selectAIChatProgressSourceHost = createSelector(
  [selectActiveAIResponse],
  response => response?.progressSuppressed || response?.progress?.stage === 'finished'
    ? undefined : response?.progress?.sourceHost
);

export const selectAIChatProgressCitations = createSelector(
  [selectActiveAIResponse],
  response => {
    const progress = response?.progress;
    return response?.progressSuppressed || progress?.stage === 'finished'
      || progress?.citationsFound === undefined || progress.citationsRetained === undefined
      ? undefined : { found: progress.citationsFound, retained: progress.citationsRetained };
  }
);

export const selectAIChatbotConversations = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.conversations
);

export const selectAIResponseStatus = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.aiResponseStatus
);

export const selectHtmlSnapshotResponses = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.htmlSnapshotResponses
);

/** Action choices for the latest completed assistant response, if the member has not replied yet. */
export const selectLatestAIActionOffer = createSelector(
  [getAIChatbotConversationsState],
  (appState) => {
    const latest = appState.conversations[appState.conversations.length - 1];
    if (!latest || latest.isSent || !latest.responseId) return undefined;
    const response = appState.htmlSnapshotResponses[latest.responseId];
    if (!response?.actionOffer || !response.isComplete || response.streamingStopped
      || appState.aiResponseStatus !== AIResponseStatusType.READY) return undefined;
    return { responseId: response.responseId, ...response.actionOffer };
  }
);

export const selectSuggestedQuestions = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.suggestedQuestions
);

export const selectConversationSessionId = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.conversationSessionId
);

export const selectIsChatbotExternalOpen = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.isExternalOpen
);

export const selectPendingRoomMayaOpenId = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.pendingRoomMayaOpenId
);

export const selectLoadingPrompts = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.loadingPrompts
);

export const selectIsSummaryMode = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.isSummaryMode
);

export const selectContextMode = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.contextMode
);

export const selectActiveChatbotVariant = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.activeChatbotVariant
);

export const selectActiveSubjectLabel = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.activeSubjectLabel
);

export const selectOnboardingSection = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.onboardingSection
);

export const selectOnboardingOverview = createSelector(
  [getAIChatbotConversationsState],
  (appState) => appState.onboardingOverview
);
